import { useState, useEffect } from 'react';
import { isBusyBookingStatus } from '../../util/studioAvailability';

export const useTimeBlockingLogic = (
  studioId,
  bookingData,
  blockedTimes,
  createBlockedTime,
  deleteBlockedTime
) => {
  const today = new Date().toISOString().split('T')[0];
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("23:59");
  const [isFullDay, setIsFullDay] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [overlappingTimes, setOverlappingTimes] = useState([]);

  useEffect(() => {
    if (startDate) {
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setEndDate(nextDay.toISOString().split('T')[0]);
    }
  }, [startDate]);

  const getDateTime = (date, time) => {
    if (!date || !time) return null;
    return new Date(`${date}T${time}`);
  };

  const startDateTime = getDateTime(startDate, startTime);
  const endDateTime = getDateTime(endDate, endTime);

  const checkBookingConflicts = () => {
    if (!bookingData || !Array.isArray(bookingData) || bookingData.length === 0) {
      return false;
    }
    
    if (!startDateTime || !endDateTime) return false;
    
    // Filter bookings that overlap with our date range
    const conflictingBookings = bookingData.filter(booking => {
      if (!booking.date) return false;
      
      const [day, month, year] = booking.date.split('.');
      const bookingDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      const startDateOnly = new Date(startDateTime);
      startDateOnly.setHours(0, 0, 0, 0);
      
      const endDateOnly = new Date(endDateTime);
      endDateOnly.setHours(23, 59, 59, 999);
      
      const isActiveBooking = isBusyBookingStatus(booking.status);
      
      const isWithinBlockedRange = bookingDate >= startDateOnly && bookingDate <= endDateOnly;
      
      return isActiveBooking && isWithinBlockedRange;
    });
    
    return conflictingBookings.length > 0 ? conflictingBookings : false;
  };

  const checkBlockedTimeConflicts = () => {
    if (!blockedTimes?.blockedTimesByStudioID?.items || 
        !Array.isArray(blockedTimes.blockedTimesByStudioID.items) ||
        blockedTimes.blockedTimesByStudioID.items.length === 0) {
        return false;
    }
    
    if (!startDateTime || !endDateTime) {
        return false;
    }
    
    const conflictingBlockedTimes = blockedTimes.blockedTimesByStudioID.items.filter(blockedTime => {
        const blockedStartTime = new Date(blockedTime.startDateTime);
        const blockedEndTime = new Date(blockedTime.endDateTime);

        const hasOverlap = (startDateTime <= blockedEndTime && endDateTime >= blockedStartTime);
        return hasOverlap;
    });
    
    return conflictingBlockedTimes.length > 0 ? conflictingBlockedTimes : false;
  };

  const handleMergeAndReplace = async (onSuccess) => {
    try {
      setIsLoading(true);
      
      const allExistingBlocks = blockedTimes?.blockedTimesByStudioID?.items || [];
      
      let mergedStartDateTime = new Date(startDateTime);
      let mergedEndDateTime = new Date(endDateTime);
      
      allExistingBlocks.forEach(block => {
        const blockStart = new Date(block.startDateTime);
        const blockEnd = new Date(block.endDateTime);
        
        if (blockStart < mergedStartDateTime) {
          mergedStartDateTime = blockStart;
        }
        
        if (blockEnd > mergedEndDateTime) {
          mergedEndDateTime = blockEnd;
        }
      });
      
      const deletePromises = [];
      
      for (const block of allExistingBlocks) {
        const promise = deleteBlockedTime(block.id)
          .then(result => {
            return result;
          })
          .catch(err => {
            return null;
          });
              
        deletePromises.push(promise);
      }
      
      await Promise.allSettled(deletePromises);
      
      const newBlock = {
        studioID: studioId,
        title: title || "Blocked Time",
        startDateTime: mergedStartDateTime.toISOString(),
        endDateTime: mergedEndDateTime.toISOString(),
        isFullDay
      };
      
      await createBlockedTime(newBlock);
      
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (err) {
      setError("Blockierte Zeiten konnten nicht zusammengeführt werden.");
    } finally {
      setIsLoading(false);
      setOverlappingTimes([]);
      resetForm();
    }
  };

  const resetForm = () => {
    setStartDate(today);
    setTitle("");
    setIsFullDay(false);
    setStartTime("09:00");
    setEndTime("23:59");
  };

  const handleCreateBlockedTime = async (onSuccess, replaceExisting = false) => {
    try {
      setError(null);
      setIsLoading(true);
      
      if (!startDateTime || !endDateTime) {
        setError("Bitte wählen Sie gültige Daten und Zeiten");
        return;
      }
      
      if (endDateTime < startDateTime) {
        setError("Ende muss nach Anfang sein");
        return;
      }

      const conflicts = checkBookingConflicts();
      if (conflicts) {
        setError("Es gibt bereits Buchungen in diesem Zeitraum.");
        return;
      }
      
      if (!replaceExisting) {
        const blockedConflicts = checkBlockedTimeConflicts();
        
        if (blockedConflicts && blockedConflicts.length > 0) {
          setIsLoading(false);
          
          setOverlappingTimes(blockedConflicts);
          
          const confirmed = window.confirm(
            `Der gewählte Zeitraum überschneidet sich mit ${blockedConflicts.length} bestehenden blockierten Zeiträumen. Möchten Sie einen neuen zusammengeführten Zeitraum erstellen?`
          );
          
          if (confirmed) {
            await handleMergeAndReplace(onSuccess);
          }
          
          return;
        }
      }
      
      const blockedTimeInput = {
        studioID: studioId,
        title: title || "Blocked Time",
        startDateTime: startDateTime.toISOString(),
        endDateTime: endDateTime.toISOString(),
        isFullDay
      };
      
      await createBlockedTime(blockedTimeInput);
      
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      
      resetForm();
    } catch (err) {
      console.error("Blockierte Zeit konnte nicht erstellt werden:", err);
      setError("Blockierte Zeit konnte nicht erstellt werden.");
    } finally {
      setOverlappingTimes([]);
      setIsLoading(false);
    }
  };

  return {
    title,
    setTitle,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    isFullDay,
    setIsFullDay,
    error,
    setError,
    isLoading,
    overlappingTimes,
    handleCreateBlockedTime,
    handleMergeAndReplace,
    checkBookingConflicts,
    checkBlockedTimeConflicts,
    resetForm
  };
};

export default useTimeBlockingLogic;
